/**
 * customers-config — shared Customers-screen types + the served-vocabulary
 * adapter.
 *
 * Docket 2026-07-26 §1.2.2: the per-industry vocabulary that lived here as a
 * resolved lookup (`INDUSTRY` keyed by a literal tenant-slug map,
 * `SLUG_INDUSTRY` / `industryForTenant`) is DELETED. Customer tiers, entity
 * nouns, column/KPI labels and currency posture arrive through the typed
 * @v0y4ge/api-client (`getTenantVocabulary`, REQ-091), backed by
 * @v0y4ge/api-mocks; the screen renders empty until the catalog resolves.
 * The tenant-slug→vertical mapping never exists as a literal in app source.
 *
 * What remains here is structural: the record/status types every consumer
 * shares, and `useCustomersVocab()` — the adapter that turns the served
 * vocabulary DATA into the presentation shape the screen consumes (functions
 * can't be served; status colours are semantic UI tokens, not tenant data).
 *
 * Records are NEVER authored here (no-seed-data rule) — they come from
 * listCustomers (preview-seeded per tenant). Brand accent/ink are the
 * TENANT's configured accents (documented exception to the token rule —
 * content, not system chrome, same as so-kit).
 */
import { T, alpha } from "@v0y4ge/jrni-ui";
import { useTenantVocab, formatVocabMoney, type CustomersVocabData } from "../../lib/tenant-vocab";

export type ChannelKey = "email" | "website" | "mobile" | "event" | "phone" | "instore";
export type ApptStatus = "completed" | "upcoming" | "cancelled" | "noshow";
export type QueueStatus = "served" | "left" | "converted";
export type WaitStatus = "waiting" | "offered" | "converted" | "expired";

export interface CustPrefs { store: string; day: string; time: string; channel: string; category: string; }
export interface CustAppt { svc: string; advisor: string; store: string; date: string; status: ApptStatus; via: ChannelKey; }
export interface CustEventReg { title: string; date: string; status: "checkedin" | "registered"; drove: boolean; }
export interface CustQueue { store: string; reason: string; date: string; waitMin: number; status: QueueStatus; }
export interface CustWait { svc: string; advisor?: string; store: string; window: string; date: string; status: WaitStatus; position?: number; }
export interface CustCard { product: string; limit: number; balance: number; }
export interface CustomerRecord {
  id: string; name: string; initials: string; tier: string; store: string; since: string; langs: string;
  ltv: number; ytd: number; prefs: CustPrefs;
  appts: CustAppt[]; events: CustEventReg[]; queue: CustQueue[]; waitlist?: CustWait[]; card?: CustCard | null;
}

export interface TierMeta { label: string; bg: string; fg: string; }
export interface ChannelMeta { label: string; icon: string; tint: string; }
export interface EvtOption { id: string; title: string; date: string; location: string; }

/** The presentation shape the Customers screen consumes — adapted from the
 * served vocabulary, no longer selected from a baked per-vertical record. */
export interface IndustryConfig {
  accent: string; accentSoft: string; ink: string;
  entity: string; entityPlural: string;
  money: (n: number) => string;
  cardMoney: (n: number) => string;
  tiers: Record<string, TierMeta>;
  tierOrder: string[];
  channels: Record<ChannelKey, ChannelMeta>;
  apptStatus: Record<ApptStatus, { label: string; fg: string; bg: string }>;
  queueStatus: Record<QueueStatus, { label: string; c: string; bg: string }>;
  columns: { store: string; ltv: string };
  kpis: { customers: string; appointments: string; appointmentsSub: string; events: string; eventsSub: string; queue: string; queueSub: string };
  prefsLabels: { store: string; day: string; time: string; channel: string; category: string; ytd: string };
  otb: { hasCardTitle: string; metricSub: string; balanceWord: string; limitWord: string; noCardTitle: string; noCardSub: string; inviteBtn: string; inviteToastSub: string };
  searchPlaceholder: string;
  exportTitle: string;
  emailDomain: string;
  phoneShown: (id: string) => string;
}

/* Channel icons + tints are SEMANTIC UI (identical across tenants; only the
 * labels are vocabulary). `accent` tints resolve against the served accent. */
const CHANNEL_UI: Record<ChannelKey, { icon: string; tint: "accent" | string }> = {
  email: { icon: "mail", tint: T.tDarkBlue },
  website: { icon: "browser", tint: "accent" },
  mobile: { icon: "phone", tint: T.tPurple },
  event: { icon: "calendar", tint: T.tTurquoise },
  phone: { icon: "phone", tint: T.tOrange },
  instore: { icon: "bell", tint: T.soft },
};

function adapt(d: CustomersVocabData): IndustryConfig {
  const tiers: Record<string, TierMeta> = {};
  d.tiers.forEach((t) => { tiers[t.id] = { label: t.label, bg: t.bg || T.n6, fg: t.fg || T.soft }; });
  const channels = {} as Record<ChannelKey, ChannelMeta>;
  (Object.keys(CHANNEL_UI) as ChannelKey[]).forEach((k) => {
    const ui = CHANNEL_UI[k];
    channels[k] = { label: d.channelLabels[k] ?? k, icon: ui.icon, tint: ui.tint === "accent" ? d.accent : ui.tint };
  });
  return {
    accent: d.accent, accentSoft: d.accentSoft, ink: d.ink,
    entity: d.entity, entityPlural: d.entityPlural,
    money: (n) => formatVocabMoney(d.money, n),
    cardMoney: (n) => formatVocabMoney(d.cardMoney, n),
    tiers,
    tierOrder: d.tiers.map((t) => t.id),
    channels,
    apptStatus: {
      completed: { label: "Completed", fg: T.green1, bg: alpha(T.green1, 12) },
      upcoming: { label: "Upcoming", fg: T.tDarkBlue, bg: alpha(T.tDarkBlue, 12) },
      cancelled: { label: "Cancelled", fg: T.soft, bg: T.n6 },
      noshow: { label: "No-show", fg: T.red1, bg: alpha(T.red1, 10) },
    },
    queueStatus: {
      served: { label: d.queueServedLabel, c: T.green1, bg: alpha(T.green1, 12) },
      left: { label: "Left queue", c: T.red1, bg: alpha(T.red1, 10) },
      converted: { label: "Booked instead", c: d.accent, bg: d.accentSoft },
    },
    columns: d.columns, kpis: d.kpis, prefsLabels: d.prefsLabels, otb: d.otb,
    searchPlaceholder: d.searchPlaceholder,
    exportTitle: d.exportTitle,
    emailDomain: d.emailDomain,
    // Display formatting of the record id behind the served prefix — REQ-091
    // notes the canonical home for contact info is the customer record itself.
    phoneShown: (id) => d.phonePrefix + " " + String(1000 + ((parseInt(id.replace(/\D/g, "").slice(-3), 10) || 0))).slice(-4),
  };
}

/** Served customers vocabulary for the active tenant — null until resolved
 * (the screen renders its loading/empty state, never another tenant's nouns). */
export function useCustomersVocab(): IndustryConfig | null {
  const vocab = useTenantVocab();
  return vocab?.customers ? adapt(vocab.customers) : null;
}
